import { memoize } from 'util/memoization'
import { createSelector } from 'reselect'

import { getActorCurried } from 'util/actors'
import { getTwitterUsernames } from 'util/ticket'
import { emptyArr } from 'util/arrays'

import { selectCustomersById } from 'ducks/customers/selectors'
import { selectCurrentTicket } from 'selectors/tickets/current'

import { selectTicketDraft } from './drafts'

// This is to guarantee that if recipients don't change,
// we still get the same object
const recipientSetBuilder = memoize(({ to, cc, bcc }) => {
  return {
    to,
    cc,
    bcc,
  }
})

const recipientExtractor = memoize((customersById, draft) => {
  const getCustomer = getActorCurried({}, customersById)
  return recipientSetBuilder({
    to: (draft && draft.to && draft.to.map(getCustomer)) || emptyArr,
    cc: (draft && draft.cc && draft.cc.map(getCustomer)) || emptyArr,
    bcc: (draft && draft.bcc && draft.bcc.map(getCustomer)) || emptyArr,
  })
})

export const selectRecipients = createSelector(
  selectTicketDraft,
  selectCustomersById,
  (draft, customersById) => {
    return recipientExtractor(customersById, draft)
  }
)

const selectAllRecipients = createSelector(
  selectRecipients,
  draftRecipients => {
    return [].concat(
      draftRecipients.to,
      draftRecipients.cc,
      draftRecipients.bcc
    )
  }
)

export const selectReplyRecipient = createSelector(
  selectRecipients,
  recipients => recipients.to
)

export const selectTwitterRecipients = createSelector(
  selectRecipients,
  selectCurrentTicket,
  (recipients, ticket) => {
    const handles = getTwitterUsernames(ticket)
    const allRecipients = []
    const { to } = recipients

    if (recipients.to) {
      const first = Array.isArray(to) ? to[0] : to
      allRecipients.push(first)
    }

    handles.forEach(handle => {
      if (handle) {
        const username = handle.substr(1)
        if (
          allRecipients.length > 0 &&
          allRecipients[0].twitter &&
          allRecipients[0].twitter.username !== username
        ) {
          allRecipients.push({
            twitter: {
              username,
            },
          })
        }
      }
    })
    return allRecipients
  }
)

export const selectAllRecipientIds = createSelector(selectAllRecipients, all =>
  all.map(recipient => recipient.id)
)
